import React, { useCallback, useRef, useEffect } from 'react'
import { Box, useScreenClass } from '@vp/swan'
import { NewArrivalsType } from '../../types/TemplateConfiguration'
import {
  PageLevel,
  ProductRecommendationItem,
  ProductRecommendationsList
} from '../../types/Recommendations'
import { Lane } from '../Lane/Lane'
import { type ExtendedMtsLocalizationResponse } from '@vp/ep-tiles'
import { DebugPageSections } from '../debugComponents/DebugPageSections'
import {
  sortProductsByStock,
  getDesktopTilesPerRow,
  getEffectiveTilesPerRow
} from '../../utils/recommendations/productRecommendationsBuilder'
import {
  onProductClick,
  onProductListView,
  type AdaptedResponse
} from '@vp/product-recommendations-tracking'
import { trackClickEvent } from '../../utils/tracking/tracking'
import { useAppContext } from '../../state/AppContext'

const MAX_NEW_ARRIVALS_TILES = 10

interface NewArrivalsProps {
  section: NewArrivalsType
  sectionNumber: number
  productRecommendations: ProductRecommendationsList | null
  adaptedResponse?: AdaptedResponse | null
  pageLevel?: PageLevel | null
  sidebarEnabled?: boolean
  calloutsLocalization: ExtendedMtsLocalizationResponse
}

const NewArrivals = ({
  section,
  sectionNumber,
  productRecommendations,
  adaptedResponse,
  pageLevel,
  sidebarEnabled = false,
  calloutsLocalization
}: NewArrivalsProps) => {
  const { merchandisingCategoryId } = useAppContext()
  const screenClass = useScreenClass()

  if (!productRecommendations || productRecommendations.products.length === 0) {
    return null
  }

  const isL2 = pageLevel === 'L2'
  const products = sortProductsByStock(productRecommendations.products).slice(
    0,
    MAX_NEW_ARRIVALS_TILES
  )

  const handleProductClick = useCallback(
    (product: ProductRecommendationItem, index: number) => {
      trackClickEvent({
        categoryId: merchandisingCategoryId,
        pageZone: 'New Arrivals',
        ctaValue: product.mpvId,
        destinationUrl: product.productUrl,
        index: index + 1
      })

      if (!adaptedResponse) return
      const trackingProduct = adaptedResponse.products.find((p) => p.mpvId === product.mpvId)
      if (!trackingProduct) return
      onProductClick({
        adaptedResponse,
        product: trackingProduct,
        clickedElement: 'Tile',
        urlId: window.location.pathname,
        recommendationsPlacement: adaptedResponse.placement?.selector ?? ''
      })
    },
    [adaptedResponse, merchandisingCategoryId]
  )

  // --- Visibility-based PLV tracking ---
  const sectionRef = useRef<HTMLDivElement>(null)
  const reportedRef = useRef<Set<number>>(new Set())
  const previousFirstRef = useRef<number>(0)
  const plvSetRef = useRef<number>(0)

  // Fire initial PLV when 50% of the section is visible in the viewport
  useEffect(() => {
    if (plvSetRef.current > 0 || !adaptedResponse || !sectionRef.current) return

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting || plvSetRef.current > 0) return
        plvSetRef.current = 1

        const tilesPerRow = getEffectiveTilesPerRow(
          getDesktopTilesPerRow(products.length, sidebarEnabled),
          screenClass
        )
        const visibleCount = Math.min(tilesPerRow, products.length)
        const initialIndices = Array.from({ length: visibleCount }, (_, i) => i)
        initialIndices.forEach((i) => reportedRef.current.add(i))

        onProductListView({
          adaptedResponse,
          screenClass,
          set: 1,
          urlId: window.location.pathname,
          recommendationsPlacement: adaptedResponse.placement?.selector ?? '',
          visibleProductIndices: initialIndices
        })

        observer.disconnect()
      },
      { threshold: 0.5 }
    )

    observer.observe(sectionRef.current)

    return () => observer.disconnect()
  }, [adaptedResponse, products.length, sidebarEnabled, screenClass])

  // Handle carousel slide changes (right-arrow only, deduplicated)
  const handleSlideChange = useCallback(
    (firstSlide: number, lastSlide: number) => {
      if (!adaptedResponse) return

      // Convert 1-based (SWAN) to 0-based indices
      const first = firstSlide - 1
      const last = lastSlide - 1

      // Left-arrow: firstSlide moved backwards — skip
      if (first < previousFirstRef.current) {
        previousFirstRef.current = first
        return
      }
      previousFirstRef.current = first

      // Collect only unreported product indices
      const newIndices: number[] = []
      for (let i = first; i <= last; i++) {
        if (!reportedRef.current.has(i)) {
          newIndices.push(i)
          reportedRef.current.add(i)
        }
      }

      if (newIndices.length === 0) return

      plvSetRef.current += 1

      onProductListView({
        adaptedResponse,
        screenClass,
        set: plvSetRef.current,
        urlId: window.location.pathname,
        recommendationsPlacement: adaptedResponse.placement?.selector ?? '',
        visibleProductIndices: newIndices
      })
    },
    [adaptedResponse, screenClass]
  )

  return (
    <Box ref={sectionRef}>
      <Lane
        title={isL2 ? section.title?.value : undefined}
        subtitle={isL2 ? section.subtitle?.value : undefined}
        products={products}
        sidebarEnabled={sidebarEnabled}
        pageSection={`New Arrivals ${sectionNumber}`}
        calloutsLocalization={calloutsLocalization}
        sectionId={section.sectionId}
        onProductTileClick={handleProductClick}
        onSlideChange={handleSlideChange}
      />
      <DebugPageSections
        rawSectionData={{ section, productRecommendations, calloutsLocalization }}
      />
    </Box>
  )
}

export default NewArrivals
